import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'; // Import useRef
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    CardMedia,
    CircularProgress,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    Slide,
    AppBar,
    Toolbar,
    Link,
    Button,
    TextField,
    InputAdornment,
    Avatar,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosNewIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../auth/AuthContext.js';
import { Stack } from '@mui/system';
import { useParams, useNavigate } from 'react-router-dom';
import SVGComponent from './svgviewer-react-map.js';
import GroupLocation from './GroupLocation.jsx';

// Define a transition for the Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const GROUPS_PER_LOAD = 12; // Number of groups to load at a time per API call

const GroupsFeedScreen = () => {
    const { currentUser, dbUser, isLoading: authLoading } = useAuth();
    const { groupName } = useParams();
    const navigate = useNavigate();

    const [groups, setGroups] = useState([]); // Stores currently loaded groups
    const [page, setPage] = useState(1); // Current page for pagination
    const [hasMore, setHasMore] = useState(true); // Indicates if there are more groups to load
    const [loadingGroups, setLoadingGroups] = useState(false); // Loading state for API calls
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // New state for debounced term
    const [hasInitialDataProcessed, setHasInitialDataProcessed] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [openImageDialog, setOpenImageDialog] = useState(false);

    // Ref to hold the timeout ID
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        if (openDialog) {
            setCurrentImageIndex(0);
        }
    }, [openDialog, selectedGroup]);

    // Use useCallback for fetchGroups to prevent unnecessary re-renders
    const fetchGroups = useCallback(async (pageNumber, currentSearchTerm) => {
        if (!dbUser) {
            setHasInitialDataProcessed(true);
            return;
        }

        setLoadingGroups(true);
        try {
            let fetchedGroups = [];
            if (dbUser.getPublicGroups) {
                console.log('pageNumber', pageNumber, 'currentSearchTerm', currentSearchTerm, 'GROUPS_PER_LOAD', GROUPS_PER_LOAD);
                fetchedGroups = await dbUser.getPublicGroups({ pageNum: pageNumber, searchTerm: currentSearchTerm, limit: GROUPS_PER_LOAD });
            } else if (dbUser.publicGroups) {
                if (Array.isArray(dbUser.publicGroups)) {
                    const allLocalGroups = Object.values(dbUser.publicGroups);
                    const filteredLocalGroups = allLocalGroups.filter(group =>
                        group.name.toLowerCase().includes(currentSearchTerm.toLowerCase()) ||
                        group.description.toLowerCase().includes(currentSearchTerm.toLowerCase()) ||
                        (group.services && group.services.toLowerCase().includes(currentSearchTerm.toLowerCase()))
                    );
                    const startIndex = (pageNumber - 1) * GROUPS_PER_LOAD;
                    fetchedGroups = filteredLocalGroups.slice(startIndex, startIndex + GROUPS_PER_LOAD);
                    setHasMore(fetchedGroups.length === GROUPS_PER_LOAD);
                }
            }

            setGroups(prevGroups => {
                if (pageNumber === 1) {
                    return fetchedGroups;
                }
                return [...prevGroups, ...fetchedGroups];
            });

            setHasMore(fetchedGroups.length === GROUPS_PER_LOAD);

            if (groupName && pageNumber === 1) {
                const decodedGroupName = decodeURIComponent(groupName).replace(/-/g, ' ');
                const groupToOpen = await dbUser.getGroupByName(decodedGroupName);
                if (groupToOpen) {
                    setSelectedGroup(groupToOpen);
                    setOpenDialog(true);
                } else {
                    console.warn(`Profesional "${decodedGroupName}" no encontrado.`);
                    navigate('/oficiomania/professionals');
                }
            }
        } catch (error) {
            console.error('Failed to fetch public groups:', error);
            setGroups([]);
            setHasMore(false);
        } finally {
            setLoadingGroups(false);
            setHasInitialDataProcessed(true);
        }
    }, [dbUser, groupName, navigate]);

    useEffect(() => {
        // Fetch groups when component mounts or dbUser changes
        // This effect will now trigger based on debouncedSearchTerm
        if (!authLoading) {
            setGroups([]); // Clear groups when starting a new fetch
            setPage(1); // Reset page to 1
            setHasMore(true); // Assume there's more data initially
            fetchGroups(1, debouncedSearchTerm); // Use debouncedSearchTerm here
        }
    }, [authLoading, dbUser, debouncedSearchTerm]); // Trigger on debouncedSearchTerm

    // Effect for debouncing the search term
    useEffect(() => {
        // Clear previous timeout if exists
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set a new timeout
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // Adjust debounce time (e.g., 500ms) as needed

        // Cleanup function: clear timeout if component unmounts or searchTerm changes before timeout
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]); // Re-run this effect when searchTerm changes

    const handleCardClick = (group) => {
        const encodedGroupName = encodeURIComponent(group.name).replace(/ /g, '-');
        navigate(`/oficiomania/professionals/${encodedGroupName}`);
        setSelectedGroup(group);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedGroup(null);
        navigate('/oficiomania/professionals');
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === selectedGroup.images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const handlePreviousImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? selectedGroup.images.length - 1 : prevIndex - 1
        );
    };

    const handleImageClick = () => {
        setOpenImageDialog(true);
    };

    const handleCloseImageDialog = () => {
        setOpenImageDialog(false);
    };

    const handleLoadMore = () => {
        console.log('Loading more groups...');
        const nextPage = page + 1;
        setPage(nextPage);
        fetchGroups(nextPage, debouncedSearchTerm); // Use debouncedSearchTerm here
    };

    const handleSearchChange = (event) => {
        console.log('Search input changed:', event.target.value);
        setSearchTerm(event.target.value); // Only update searchTerm immediately
        // The debouncing effect will handle updating debouncedSearchTerm and triggering fetchGroups
    };

    if (authLoading || !hasInitialDataProcessed) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ marginLeft: 2 }}>Cargando grupos...</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                padding: 4,
            }}
        >
            <Container maxWidth="lg">
                <Typography variant="h3" fontWeight="bold" align="center" gutterBottom>
                    Profesionales
                </Typography>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    Encuentra carpinteros, pintores, fontaneros y más en tu zona...
                </Typography>

                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                    <TextField
                        label="Buscar profesionales"
                        variant="outlined"
                        fullWidth
                        value={searchTerm} // Bind TextField to searchTerm
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ maxWidth: 500 }}
                    />
                    <Button
                        variant=""
                        onClick={() => navigate('/oficiomania/professionalsinmap')}
                        sx={{
                            color: '#3f51b5',
                            flexShrink: 0
                        }}
                    >
                        <SVGComponent width={32} height={32} />
                    </Button>
                </Box>

                {groups.length === 0 && !loadingGroups ? (
                    <Box sx={{ textAlign: 'center', mt: 8 }}>
                        <Typography variant="h5" color="text.secondary">
                            {searchTerm ? `No se encontraron profesionales para "${searchTerm}".` : 'No se encontraron profesionales por el momento. ¡Vuelve pronto!'}
                        </Typography>
                        {!dbUser && (
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                                Por favor, inicia sesión para ver profesionales o crea tu perfil profesional.
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={4}>
                            {/* Prevent groups to be empty */}
                            {groups?.map((group) => (
                                <Grid item key={group._id || group.id} xs={12} sm={6} md={4} lg={3}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            boxShadow: 3,
                                            borderRadius: 2,
                                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: 6,
                                                cursor: 'pointer',
                                            },
                                        }}
                                        onClick={() => handleCardClick(group)}
                                    >
                                        {group.images && group.images.length > 0 && (
                                            <CardMedia
                                                component="img"
                                                height="180"
                                                image={`https://bluejims.com:5005/misbolos/image/${group.images[0]}/q/15`}
                                                alt={group.name}
                                                sx={{ borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
                                            />
                                        )}
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography gutterBottom variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                                                {group.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {group.description}
                                            </Typography>
                                            <Chip
                                                label={`Servicios: ${group.services || 'General'}`}
                                                size="small"
                                                sx={{ mt: 1, mr: 1, fontWeight: 'bold' }}
                                            />
                                            <Chip
                                                label={`Disponible: ${group.active ? 'Sí' : 'No'}`}
                                                size="small"
                                                color={group.active ? "success" : "default"}
                                                sx={{ mt: 1, fontWeight: 'bold' }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        {hasMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleLoadMore}
                                    disabled={loadingGroups} // Disable button while loading
                                    sx={{
                                        backgroundColor: '#3f51b5',
                                        '&:hover': {
                                            backgroundColor: '#303f9f',
                                        },
                                    }}
                                >
                                    {loadingGroups ? <CircularProgress size={24} color="inherit" /> : 'Cargar Más'}
                                </Button>
                            </Box>
                        )}
                        {loadingGroups && groups.length > 0 && ( // Show spinner below button if more groups are loading
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                    </>
                )}
            </Container>

            {/* Group Detail Dialog */}
            <Dialog
                fullScreen
                open={openDialog}
                onClose={handleCloseDialog}
                TransitionComponent={Transition}
            >
                <AppBar sx={{ position: 'relative', background: 'linear-gradient(to right,rgb(31, 31, 31),rgb(20, 20, 20))' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleCloseDialog}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Perfil del Profesional
                        </Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent dividers sx={{ p: 0, '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                    {selectedGroup && (
                        <Box sx={{ p: 3 }}>
                            {selectedGroup.images && selectedGroup.images.length > 0 && (
                                <Box sx={{ position: 'relative', mb: 3 }}>
                                    <CardMedia
                                        component="img"
                                        image={`https://bluejims.com:5005/misbolos/image/${selectedGroup.images[currentImageIndex]}`}
                                        alt={selectedGroup.name}
                                        sx={{
                                            maxHeight: 400,
                                            width: '100%',
                                            objectFit: 'cover',
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                        }}
                                        onClick={handleImageClick}
                                    />
                                    {selectedGroup.images.length > 1 && (
                                        <>
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: 8,
                                                    transform: 'translateY(-50%)',
                                                    color: 'white',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                    },
                                                }}
                                                onClick={handlePreviousImage}
                                                aria-label="previous image"
                                            >
                                                <ArrowBackIosNewIcon />
                                            </IconButton>
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    right: 8,
                                                    transform: 'translateY(-50%)',
                                                    color: 'white',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                    },
                                                }}
                                                onClick={handleNextImage}
                                                aria-label="next image"
                                            >
                                                <ArrowForwardIosNewIcon />
                                            </IconButton>
                                        </>
                                    )}
                                    {selectedGroup.images.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                right: 8,
                                                color: 'white',
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                borderRadius: 1,
                                                padding: '2px 6px',
                                            }}
                                        >
                                            {currentImageIndex + 1} / {selectedGroup.images.length}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
                                <Typography
                                    variant="h4"
                                    component="h2"
                                    gutterBottom
                                    sx={{ fontWeight: 'bold', color: '#3f51b5' }}
                                >
                                    {selectedGroup.name}
                                </Typography>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    target="_blank"
                                    href={`/oficiomania/weare/${encodeURIComponent(selectedGroup.name)}`}
                                    startIcon={<LanguageIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        padding: '6px 12px',
                                        backgroundColor: '#e3f2fd',
                                        color: 'black',
                                        '&:hover': {
                                            backgroundColor: '#bbdefb',
                                        },
                                        mb: 1,
                                    }}
                                >
                                    Ver perfil completo
                                </Button>
                            </Stack>


                            <Box sx={{ mb: 2 }}>
                                {selectedGroup.location && (
                                    <GroupLocation location={selectedGroup.location} />
                                )}
                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: '7px' }}>
                                    {selectedGroup.members.map((member, index) => {
                                        const imageUrl = `https://bluejims.com:5005/misbolos/image/${member.profilePicture || member.photoURL}/q/5`;
                                        return (
                                            <Chip
                                    key={index}
                                    label={member.email}
                                    clickable
                                    color="primary"
                                    avatar={
                                        <Avatar
                                            src={imageUrl}
                                            alt={member.email}
                                            sx={{
                                                border: '2px solid white',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                    }
                                    onClick={() => navigate(`/oficiomania/users/${member.email}`)}
                                />
                                        );
                                    })}
                                </Box>
                                <Chip
                                    label={`Disponible: ${selectedGroup.active ? 'Sí' : 'No'}`}
                                    color={selectedGroup.active ? "success" : "default"}
                                    sx={{ fontSize: '1rem', padding: '5px 10px', height: 'auto', fontWeight: 'bold' }}
                                />
                            </Box>

                            <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                Sobre el Profesional:
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {selectedGroup.description}
                            </Typography>

                            {selectedGroup.services && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                        Servicios Ofrecidos:
                                    </Typography>
                                    <Typography variant="body1" paragraph>
                                        {selectedGroup.services}
                                    </Typography>
                                </>
                            )}

                            {selectedGroup.links && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                        Enlaces:
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                                        {selectedGroup.links.website && (
                                            <Link href={selectedGroup.links.website} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                <LanguageIcon sx={{ mr: 0.5 }} /> Web
                                            </Link>
                                        )}
                                        {selectedGroup.links.facebook && (
                                            <Link href={selectedGroup.links.facebook} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                <FacebookIcon sx={{ mr: 0.5 }} /> Facebook
                                            </Link>
                                        )}
                                        {selectedGroup.links.instagram && (
                                            <Link href={selectedGroup.links.instagram} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                <InstagramIcon sx={{ mr: 0.5 }} /> Instagram
                                            </Link>
                                        )}
                                        {selectedGroup.links.youtube && (
                                            <Link href={selectedGroup.links.youtube} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                <YouTubeIcon sx={{ mr: 0.5 }} /> YouTube
                                            </Link>
                                        )}
                                        {selectedGroup.links.bandcamp && (
                                            <Link href={selectedGroup.links.bandcamp} target="_blank" rel="noopener" color="inherit" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                                Bandcamp
                                            </Link>
                                        )}
                                    </Stack>
                                </>
                            )}

                            {selectedGroup.activities && selectedGroup.activities.length > 0 && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5' }}>
                                        Trabajos Realizados:
                                    </Typography>
                                    <Stack spacing={1}>
                                        {selectedGroup.activities.map((activity, index) => (
                                            <Chip
                                                key={activity._id || index}
                                                label={`${activity.title} (${new Date(activity.date).toLocaleDateString('es-ES')})`}
                                                color="info"
                                                onClick={() => navigate(`/oficiomania/jobs/${activity.title}`)}
                                                sx={{ backgroundColor: 'rgb(36, 36, 36)', color: 'white', fontWeight: 'bold' }}
                                            />
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Full-Screen Image Dialog */}
            {selectedGroup && selectedGroup.images && selectedGroup.images.length > 0 && (
                <Dialog
                    fullScreen
                    open={openImageDialog}
                    onClose={handleCloseImageDialog}
                    TransitionComponent={Transition}
                >
                    <AppBar sx={{ position: 'relative', background: 'black' }}>
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleCloseImageDialog}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                Imagen de {selectedGroup.name}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <DialogContent sx={{
                        backgroundColor: 'black',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 0,
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                    }}>
                        <img
                            src={
                                selectedGroup.images.length > 0
                                    ? `https://bluejims.com:5005/misbolos/image/${selectedGroup.images[currentImageIndex]}`
                                    : 'https://source.unsplash.com/random/800x600?band,music'
                            }
                            alt={selectedGroup.name}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};

export default GroupsFeedScreen;